/**
 * CetusEd NextGen — the DATA-route boundary (⛔2 FAIL CLOSED, ⛔4 NO FICTION).
 *
 * Wraps any route that reads or writes STUDENT DATA. It lazy-initializes the
 * post-auth data stack (useDataStack) and renders its children ONLY once the
 * stack actually reports `ready`. While the stack refuses — which it does today,
 * because the encrypted storage gateway is an honest refusal (TASK-00066) — the
 * route shows an honest "not available yet" and renders NONE of the wrapped
 * student-data content.
 *
 * This is where the fail-closed guarantee lives for data routes: the login page
 * and shell render without the data stack, but a data route cannot. There is no
 * "assume ready" branch — an unproven data stack renders the unavailable notice,
 * never the children.
 */
import type { CSSProperties, ReactNode } from 'react'
import { useDataStack } from '../../core/data/useDataStack'
import { SessionLoading } from '../auth/SessionLoading'

export function DataStackBoundary({ children }: { children: ReactNode }) {
  const { status, error } = useDataStack()

  if (status === 'initializing') {
    return <SessionLoading label="Preparing the secure student-data store…" />
  }

  if (status !== 'ready') {
    // status === 'unavailable' — the data stack refused. Fail closed: show the
    // real reason, render no student data, offer no fabricated content.
    return (
      <main role="alert" aria-live="assertive" style={styles.wrap}>
        <div style={styles.card}>
          <h1 style={styles.heading}>Student data isn&rsquo;t available yet</h1>
          <p style={styles.body}>
            This feature depends on the encrypted student-data store, which is not wired up in this
            build. It will not show or save student records until that store is implemented.
          </p>
          {error ? <p style={styles.detail}>{error}</p> : null}
        </div>
      </main>
    )
  }

  return <>{children}</>
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1.5rem',
  },
  card: {
    maxWidth: '34rem',
    padding: '1.75rem',
    borderRadius: '0.75rem',
    border: '1px solid #fcd34d',
    background: '#fffbeb',
    color: '#0f2a4a',
    font: '400 1rem system-ui, sans-serif',
  },
  heading: { margin: '0 0 0.6rem', fontSize: '1.2rem' },
  body: { margin: '0 0 0.6rem', lineHeight: 1.5 },
  detail: { margin: 0, fontSize: '0.85rem', color: '#92400e' },
}
